import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormHelperText,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { getGoogleAuthUrl, registerUser } from '../api/services';
import { ErrorDisplay, GoogleAuthButton } from '../components/common';
import { createRegisterFormSchema, registerFormDefaultValues, type RegisterFormValues } from '../features/auth/registerForm';
import { useI18n } from '../i18n';
import { useAuthentication } from '../store/AppContext';
import { DESIGN_TOKENS } from '../styles/theme';
import { rememberGoogleAuthIntent } from './AuthCallbackPage';

export default function RegisterPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { loginUser: setUserLoggedIn, isAuthenticated } = useAuthentication();
  const registerFormSchema = useMemo(() => createRegisterFormSchema(t), [t]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account');
    }
  }, [isAuthenticated, navigate]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
  } = useForm<RegisterFormValues>({
    defaultValues: registerFormDefaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(registerFormSchema),
  });

  const handleRegisterSubmit = async (formData: RegisterFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const authResponse = await registerUser({
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
      });

      setUserLoggedIn(authResponse.user, authResponse.jwt);
      navigate('/first-steps');
    } catch (err: any) {
      setSubmitError(err.message || t('register.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRegister = () => {
    if (!getValues('acceptTerms')) {
      setError('acceptTerms', {
        type: 'manual',
        message: t('register.acceptTerms'),
      });
      return;
    }

    setSubmitError(null);
    rememberGoogleAuthIntent('register');
    window.location.assign(getGoogleAuthUrl());
  };

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h2" gutterBottom>
            {t('register.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('register.subtitle')}
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 3, sm: 4 } }}>
          <CardContent sx={{ p: 0 }}>
            <Box component="form" onSubmit={handleSubmit(handleRegisterSubmit)} noValidate>
              {submitError && (
                <Box sx={{ mb: 2.5 }}>
                  <ErrorDisplay message={submitError} />
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                {t('forms.accountDetails')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('forms.usernameRequired')}
                    error={Boolean(errors.username)}
                    helperText={errors.username?.message}
                    {...register('username')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('forms.emailRequired')}
                    type="email"
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    {...register('email')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('forms.passwordRequired')}
                    type="password"
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    {...register('password')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('forms.repeatPassword')}
                    type="password"
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword?.message}
                    {...register('confirmPassword')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Controller
                  name="acceptTerms"
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            {t('register.acceptPrefix')}{' '}
                            <Link
                              component={RouterLink}
                              to="/terms-of-service"
                              target="_blank"
                              sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.primary, textDecoration: 'underline' }}
                            >
                              {t('content.terms')}
                            </Link>{' '}
                            oraz{' '}
                            <Link
                              component={RouterLink}
                              to="/privacy-policy"
                              target="_blank"
                              sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.primary, textDecoration: 'underline' }}
                            >
                              {t('content.privacy')}
                            </Link>
                          </Typography>
                        }
                      />
                      {errors.acceptTerms?.message && (
                        <FormHelperText error sx={{ ml: 1.75 }}>
                          {errors.acceptTerms.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{ py: 1.5, mt: 4 }}
              >
                {isSubmitting ? t('register.submitting') : t('register.title')}
              </Button>

              <Divider sx={{ my: 2.5 }} />

              <GoogleAuthButton
                label={t('register.googleRegister')}
                onClick={handleGoogleRegister}
                disabled={isSubmitting}
              />

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="body2" align="center" color="text.secondary">
                {t('register.alreadyHaveAccount')}{' '}
                <Typography
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {t('auth.signIn')}
                </Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
